import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";
import { buildQueryString } from "@/shared/utils/buildQueryString";

import { VERIFICATION_API_ENDPOINTS } from "../constants/verification.constants";
import type {
  AccountVerificationResponse,
  CaptureStartResponse,
  CreateIdentityRequest,
  CreateVerificationResponse,
  IdentityChangeWindowResponse,
  IdentityDocumentResponse,
  IssueVerificationLinkResponse,
  OrganizationIdentityStatusResponse,
  RefreshVerificationLinkResponse,
  VerificationConsentRequest,
  VerificationConsentResponse,
  VerificationSessionResponse,
  VerificationStatusResponse,
} from "../types/verification-api.types";

// The public flow (marco B) authenticates with the identity token carried by the
// `/verify/:token` link, sent per request. The HttpApiService interceptor preserves
// an Authorization header already present on the config (see httpApi.service.ts), so
// it never clobbers this one. Still routed only through HttpApiService (rule 1).
function identityAuth(identityToken: string) {
  return { headers: { Authorization: `Bearer ${identityToken}` } };
}

export class VerificationService {
  // ── Public flow (marco B · identity token) ──

  static getSession(identityToken: string): Promise<AxiosResponse<VerificationSessionResponse>> {
    return HttpApiService.get<VerificationSessionResponse>(
      VERIFICATION_API_ENDPOINTS.PUBLIC_SESSION,
      identityAuth(identityToken),
    );
  }

  static submitConsent(
    identityToken: string,
    body: VerificationConsentRequest,
  ): Promise<AxiosResponse<VerificationConsentResponse>> {
    return HttpApiService.post<VerificationConsentResponse, VerificationConsentRequest>(
      VERIFICATION_API_ENDPOINTS.PUBLIC_CONSENT,
      body,
      identityAuth(identityToken),
    );
  }

  static startCapturePublic(identityToken: string): Promise<AxiosResponse<CaptureStartResponse>> {
    return HttpApiService.post<CaptureStartResponse>(
      VERIFICATION_API_ENDPOINTS.PUBLIC_CAPTURE_START,
      undefined,
      identityAuth(identityToken),
    );
  }

  static getStatusPublic(identityToken: string): Promise<AxiosResponse<VerificationStatusResponse>> {
    return HttpApiService.get<VerificationStatusResponse>(
      VERIFICATION_API_ENDPOINTS.PUBLIC_STATUS,
      identityAuth(identityToken),
    );
  }

  static refreshLink(identityToken: string): Promise<AxiosResponse<RefreshVerificationLinkResponse>> {
    return HttpApiService.post<RefreshVerificationLinkResponse>(
      VERIFICATION_API_ENDPOINTS.PUBLIC_LINK_REFRESH,
      undefined,
      identityAuth(identityToken),
    );
  }

  // ── Authenticated flow (marcos A/C · JWT session) ──

  static createPerson(payload: CreateIdentityRequest): Promise<AxiosResponse<CreateVerificationResponse>> {
    return HttpApiService.post<CreateVerificationResponse, CreateIdentityRequest>(
      VERIFICATION_API_ENDPOINTS.CREATE_PERSON,
      payload,
    );
  }

  static createBusiness(payload: CreateIdentityRequest): Promise<AxiosResponse<CreateVerificationResponse>> {
    return HttpApiService.post<CreateVerificationResponse, CreateIdentityRequest>(
      VERIFICATION_API_ENDPOINTS.CREATE_BUSINESS,
      payload,
    );
  }

  static getStatus(identityId: string): Promise<AxiosResponse<VerificationStatusResponse>> {
    const url = buildQueryString(VERIFICATION_API_ENDPOINTS.INTERNAL_STATUS, {
      identity_id: identityId,
    });
    return HttpApiService.get<VerificationStatusResponse>(url);
  }

  static startCapture(identityId: string): Promise<AxiosResponse<CaptureStartResponse>> {
    return HttpApiService.post<CaptureStartResponse>(
      VERIFICATION_API_ENDPOINTS.INTERNAL_CAPTURE_START(identityId),
      undefined,
    );
  }

  static issueLink(identityId: string): Promise<AxiosResponse<IssueVerificationLinkResponse>> {
    return HttpApiService.post<IssueVerificationLinkResponse>(
      VERIFICATION_API_ENDPOINTS.ISSUE_LINK(identityId),
      undefined,
    );
  }

  static getDocument(identityId: string): Promise<AxiosResponse<IdentityDocumentResponse>> {
    return HttpApiService.get<IdentityDocumentResponse>(VERIFICATION_API_ENDPOINTS.DOCUMENT(identityId));
  }

  static getOrganizationIdentityStatus(
    organizationId: string,
  ): Promise<AxiosResponse<OrganizationIdentityStatusResponse>> {
    return HttpApiService.get<OrganizationIdentityStatusResponse>(
      VERIFICATION_API_ENDPOINTS.ORGANIZATION_IDENTITY_STATUS(organizationId),
    );
  }

  // ── Account verification (the organization's own KYC/KYB · JWT) ──

  /**
   * Members never pass `organizationId` — the server pins them to their organization. The parameter exists
   * for an INTERNAL reader looking at a client organization.
   */
  static getAccountVerification(
    organizationId?: string,
  ): Promise<AxiosResponse<AccountVerificationResponse>> {
    const url = organizationId
      ? buildQueryString(VERIFICATION_API_ENDPOINTS.ACCOUNT_VERIFICATION, { organization_id: organizationId })
      : VERIFICATION_API_ENDPOINTS.ACCOUNT_VERIFICATION;
    return HttpApiService.get<AccountVerificationResponse>(url);
  }

  /** Member: ask operations to lift the identity-change cooldown early (an alert, nothing persisted). */
  static requestIdentityChange(): Promise<AxiosResponse<IdentityChangeWindowResponse>> {
    return HttpApiService.post<IdentityChangeWindowResponse>(
      VERIFICATION_API_ENDPOINTS.ACCOUNT_IDENTITY_CHANGE_REQUEST,
      undefined,
    );
  }

  /** INTERNAL staff: lift the identity-change cooldown of one organization. */
  static waiveIdentityChangeCooldown(
    organizationId: string,
  ): Promise<AxiosResponse<IdentityChangeWindowResponse>> {
    return HttpApiService.post<IdentityChangeWindowResponse>(
      VERIFICATION_API_ENDPOINTS.ACCOUNT_IDENTITY_CHANGE_WAIVER,
      {
        organization_id: organizationId,
      },
    );
  }
}
